import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { BarChart3, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const money = (value) => `CHF ${(Number(value) || 0).toFixed(2)}`;
const months = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];

export default function FinanceOverviewPage() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('this-year');
  const [customer, setCustomer] = useState('all');
  const [loading, setLoading] = useState(true);
  const [savingExpense, setSavingExpense] = useState(false);
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().split('T')[0],
    vendor: '',
    category: '',
    amount: '',
    mwst_amount: '',
    status: 'paid',
  });

  useEffect(() => {
    setLoading(true);
    api.get(`/finance/overview?period=${period}&customer=${encodeURIComponent(customer)}`)
      .then(res => setData(res.data))
      .catch(() => toast.error('Einnahmenübersicht konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [period, customer]);

  const reload = async () => {
    const res = await api.get(`/finance/overview?period=${period}&customer=${encodeURIComponent(customer)}`);
    setData(res.data);
  };

  const saveExpense = async () => {
    if (!expenseForm.date || !expenseForm.amount) {
      toast.error('Datum und Betrag sind Pflicht');
      return;
    }
    setSavingExpense(true);
    try {
      await api.post('/expenses', {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount) || 0,
        mwst_amount: parseFloat(expenseForm.mwst_amount) || 0,
      });
      toast.success('Ausgabe gespeichert');
      setExpenseForm({
        date: new Date().toISOString().split('T')[0],
        vendor: '',
        category: '',
        amount: '',
        mwst_amount: '',
        status: 'paid',
      });
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ausgabe konnte nicht gespeichert werden');
    } finally {
      setSavingExpense(false);
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm('Ausgabe löschen?')) return;
    await api.delete(`/expenses/${id}`);
    toast.success('Ausgabe gelöscht');
    await reload();
  };

  const monthly = data?.monthly || Array.from({ length: 12 }, () => 0);
  const max = Math.max(...monthly, 1);

  return (
    <Layout>
      <div className="p-6 md:p-8 bg-muted/30 min-h-full" data-testid="finance-overview-page">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-7 h-7 text-primary" strokeWidth={1.5} />
              <div>
                <h1 className="font-heading font-black text-3xl">Einnahmenübersicht</h1>
                <p className="text-sm text-muted-foreground mt-0.5">Bilanz, offene Rechnungen und bezahlte Einnahmen</p>
              </div>
            </div>
            <Button className="rounded-sm gap-2" onClick={() => window.location.href = '/invoices'}>
              <Plus className="w-4 h-4" /> Rechnungen
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mb-5">
            <div className="flex rounded-sm border border-border/70 overflow-hidden bg-card">
              <button className="px-8 h-10 bg-primary text-primary-foreground text-sm font-medium">Bilanz</button>
              <button onClick={() => window.location.href = '/invoices'} className="px-8 h-10 text-sm font-medium hover:bg-accent">Rechnungen</button>
              <button className="px-8 h-10 text-sm font-medium text-muted-foreground cursor-default">Ausgaben</button>
            </div>
            <Select value={customer} onValueChange={setCustomer}>
              <SelectTrigger className="rounded-sm w-52"><SelectValue placeholder="Alle Kunden" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Kunden</SelectItem>
                {(data?.customers || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="rounded-sm w-44"><SelectValue placeholder="Zeitraum" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="this-year">Dieses Jahr</SelectItem>
                <SelectItem value="this-month">Dieser Monat</SelectItem>
                <SelectItem value="last-year">Letztes Jahr</SelectItem>
                <SelectItem value="all">Alle Jahre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                <div className="bg-card border border-border/60 rounded-sm p-6">
                  <p className="text-sm text-muted-foreground uppercase">Rechnungen ({data?.count || 0})</p>
                  <p className="font-heading text-4xl mt-2 text-primary">{money(data?.total)}</p>
                  <p className="text-sm text-muted-foreground mt-2">MWST {money(data?.mwst)}</p>
                </div>
                <div className="bg-card border border-border/60 rounded-sm p-6">
                  <p className="text-sm text-muted-foreground uppercase">Ausgaben</p>
                  <p className="font-heading text-4xl mt-2 text-[#5b243a]">{money(data?.expenses_total)}</p>
                  <p className="text-sm text-muted-foreground mt-2">MWST {money(data?.expense_mwst)}</p>
                </div>
                <div className="bg-card border border-border/60 rounded-sm p-6">
                  <p className="text-sm text-muted-foreground uppercase">Bilanz</p>
                  <p className="font-heading text-4xl mt-2 text-emerald-600">{money(data?.balance)}</p>
                  <p className="text-sm text-muted-foreground mt-2">Offen: {money(data?.unpaid)}</p>
                </div>
              </div>

              <div className="bg-card border border-border/60 rounded-sm p-8 mb-5">
                <div className="h-80 flex items-end gap-5 border-b border-border/70 px-6">
                  {monthly.map((value, idx) => (
                    <div key={months[idx]} className="flex-1 h-full flex flex-col justify-end items-center gap-2">
                      <div
                        className="w-full max-w-9 bg-primary rounded-t-sm"
                        style={{ height: `${Math.max(3, (value / max) * 88)}%` }}
                        title={money(value)}
                      />
                      <span className="text-sm text-foreground">{months[idx]}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="bg-card border border-border/60 rounded-sm p-6">
                  <h2 className="font-heading font-semibold text-lg mb-4">Ausgaben</h2>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="space-y-1">
                      <Label>Datum</Label>
                      <Input type="date" value={expenseForm.date} onChange={e => setExpenseForm(f => ({ ...f, date: e.target.value }))} className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label>Lieferant</Label>
                      <Input value={expenseForm.vendor} onChange={e => setExpenseForm(f => ({ ...f, vendor: e.target.value }))} placeholder="z.B. UBS, Lieferant" className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label>Kategorie</Label>
                      <Input value={expenseForm.category} onChange={e => setExpenseForm(f => ({ ...f, category: e.target.value }))} placeholder="Material, Miete..." className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label>Betrag</Label>
                      <Input type="number" step="0.05" value={expenseForm.amount} onChange={e => setExpenseForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label>MWST</Label>
                      <Input type="number" step="0.05" value={expenseForm.mwst_amount} onChange={e => setExpenseForm(f => ({ ...f, mwst_amount: e.target.value }))} placeholder="0.00" className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label>Status</Label>
                      <Select value={expenseForm.status} onValueChange={v => setExpenseForm(f => ({ ...f, status: v }))}>
                        <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paid">Bezahlt</SelectItem>
                          <SelectItem value="open">Offen</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={saveExpense} disabled={savingExpense} className="rounded-sm w-full mb-4">
                    {savingExpense ? 'Speichern...' : 'Ausgabe speichern'}
                  </Button>
                  <div className="space-y-2">
                    {(data?.expenses || []).length === 0 ? (
                      <div className="h-24 border-2 border-dashed border-primary/40 rounded-sm flex flex-col items-center justify-center text-center">
                        <Search className="w-5 h-5 text-primary mb-2" />
                        <p className="text-sm text-muted-foreground">Noch keine Ausgaben erfasst.</p>
                      </div>
                    ) : data.expenses.map(expense => (
                      <div key={expense.id} className="flex items-center justify-between gap-3 text-sm bg-muted/60 rounded-sm p-3">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{expense.vendor || expense.category || 'Ausgabe'}</p>
                          <p className="text-xs text-muted-foreground">{expense.date} · {expense.category || 'Ohne Kategorie'}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-mono">{money(expense.amount)}</span>
                          <button onClick={() => deleteExpense(expense.id)} className="text-muted-foreground hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-card border border-border/60 rounded-sm p-6">
                  <h2 className="font-heading font-semibold text-lg mb-4">Ausstehende Einnahmen</h2>
                  <div className="h-12 bg-[#d95b89] text-white rounded-sm flex items-center justify-center font-bold">
                    {money(data?.unpaid)}
                  </div>
                  <h2 className="font-heading font-semibold text-lg mt-5 mb-3">Ausstehende Ausgaben</h2>
                  <div className="h-12 bg-muted rounded-sm flex items-center justify-center text-muted-foreground">
                    {data?.open_expenses ? money(data.open_expenses) : 'Alle Ausgaben sind bezahlt'}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
